import type { PostingRequirementsDef } from './requirements-key';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Tag } from '@/components/ui/tag';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { Typography } from '@/components/ui/typography';
import { Separator } from '@/components/ui/separator';

import { shouldShowField } from './display-rules';
import { PostingRequirementInput } from './posting-requirement-input';
import { useAutocompleteOptions } from './use-autocomplete-options';
import { ScreeningQuestions } from './screening-questions/screening-questions';
import { questionnaireFacet } from './direct-apply-questionnaire';
import { useQuestionnairePreview } from './screening-questions/use-questionnaire-preview';
import { useAccountSlug } from '@/hooks/use-account-slug';
import { domainMeta } from '@/utils/domain-meta';

interface PostingRequirementsProps {
  openingId: string;
  contract: PostingRequirementsDef;
  channelDisplayTitle?: string;
  logoRectangleUrl?: string | null;
  /** The board's VONQ product and channel name, for the Direct Apply preview. */
  productId?: string;
  boardName?: string;
  showOptional: boolean;
  onShowOptionalChange: (value: boolean) => void;
}

export function PostingRequirements({
  openingId,
  contract,
  channelDisplayTitle,
  logoRectangleUrl,
  productId,
  boardName,
  showOptional,
  onShowOptionalChange,
}: PostingRequirementsProps) {
  const intl = useIntl();
  const accountSlug = useAccountSlug();
  const { hookForm } = useJobPostingContext();
  const {
    watch,
    formState: { errors },
  } = hookForm;

  const requirementsKey = contract.key;
  const vonqValues =
    watch(`jobDetails.${openingId}.vonqPostingRequirements`) ?? {};
  const contractValues = vonqValues[requirementsKey] ?? {};
  const loadedOptions = useAutocompleteOptions(contract);

  const allFields = contract.posting_requirements;

  const visibleFields = allFields.filter(
    field =>
      field.type !== 'STATISCH' &&
      field.type !== 'QUESTIONNAIRE' &&
      shouldShowField(field, contractValues, allFields, loadedOptions),
  );

  const facet = questionnaireFacet(contract);
  const questionnairePreview = useQuestionnairePreview(
    openingId,
    contract,
    productId,
  );
  const showScreeningQuestions =
    !!facet && shouldShowField(facet, contractValues, allFields, loadedOptions);
  const openingSlug = watch(`jobDetails.${openingId}.slug`);

  const requiredFields = visibleFields.filter(field => field.required);
  const optionalFields = visibleFields.filter(field => !field.required);

  const emptyRequiredCount = requiredFields.filter(field => {
    const value = contractValues[field.name];
    if (Array.isArray(value)) return value.length === 0;
    return !value;
  }).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Section header */}
      <div className="flex items-center justify-between gap-2">
        <ChannelIcon
          variant="large"
          logoRectangleUrl={logoRectangleUrl}
          displayTitle={channelDisplayTitle}
        />

        {emptyRequiredCount > 0 && (
          <Tag
            className="bg-fx-amber-50"
            text={intl.formatMessage(
              {
                defaultMessage: '{count} to fill',
                description:
                  'Badge showing how many required posting requirement fields remain for this board',
              },
              { count: emptyRequiredCount },
            )}
            textClassName="text-fx-amber-800"
          />
        )}
      </div>

      {/* 2-column field grid for required fields; HTMLAREA always occupies its own full row */}
      <div className="grid grid-cols-2 gap-4">
        {requiredFields.map(field => (
          <div
            key={field.name}
            className={
              field.type === 'HTMLAREA' ? 'col-span-2 col-start-1' : undefined
            }
          >
            <PostingRequirementInput
              openingId={openingId}
              def={contract}
              field={field}
            />
          </div>
        ))}
      </div>

      {facet && showScreeningQuestions && (
        <ScreeningQuestions
          preview={questionnairePreview}
          boardName={boardName ?? channelDisplayTitle ?? ''}
          editUrl={`${domainMeta.monolithOrigin}/${accountSlug}/openings/${openingSlug}/workflow`}
          hasFacetError={
            !!errors?.jobDetails?.[openingId]?.vonqPostingRequirements?.[
              requirementsKey
            ]?.[facet.name]
          }
        />
      )}

      {optionalFields.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Button
              variant="link"
              size="link"
              type="button"
              onClick={() => onShowOptionalChange(!showOptional)}
              className="self-start"
            >
              {showOptional ? (
                <>
                  <ChevronUp className="size-4" />
                  {intl.formatMessage({
                    defaultMessage: 'Hide optional details',
                    description:
                      'Link button to collapse optional posting requirement fields for a job board',
                  })}
                </>
              ) : (
                <>
                  <ChevronDown className="size-4" />
                  {intl.formatMessage({
                    defaultMessage: 'Show optional details',
                    description:
                      'Link button to reveal optional posting requirement fields for a job board; count is the number of optional fields',
                  })}
                </>
              )}
            </Button>
            <Tag
              text={optionalFields.length}
              className="bg-fx-blue-50"
              textClassName="text-fx-blue-800"
            />
          </div>

          {showOptional && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Typography
                  variant="caption"
                  className="text-fx-neutral-400 whitespace-nowrap"
                >
                  {intl.formatMessage({
                    defaultMessage: 'Optional details',
                    description:
                      'Section label above the optional posting requirement fields for a job board',
                  })}
                </Typography>
                <Separator className="flex-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {optionalFields.map(field => (
                  <div
                    key={field.name}
                    className={
                      field.type === 'HTMLAREA'
                        ? 'col-span-2 col-start-1'
                        : undefined
                    }
                  >
                    <PostingRequirementInput
                      openingId={openingId}
                      def={contract}
                      field={field}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
